// src/services/firebaseModuleService.js
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import { db } from "../firebaseConfig";

// Konstante für das Permissions-Dokument
const MODULE_PERMISSIONS_DOC_ID = "settings";

// Default-Modulberechtigungen
const DEFAULT_PERMISSIONS = {
  dienstuebergabe: { enabled: false, sichtbarkeit: ['admin'] },
  staerkemeldung: { enabled: false, sichtbarkeit: ['user'] },
  aufgaben: { enabled: false, sichtbarkeit: ['user'] },
  feuerloescher: { enabled: false, sichtbarkeit: ['user'] },
  engeRaeume: { enabled: false, sichtbarkeit: ['user'] },
  berichte: { enabled: false, sichtbarkeit: ['user'] },
  dienstplan: { enabled: false, sichtbarkeit: ['user'] },
  freiwuenscheUrlaub: { enabled: false, sichtbarkeit: ['user'] },
  passwort: { enabled: true, sichtbarkeit: ['user'] },
  speicherorte: { enabled: true, sichtbarkeit: ['user'] },
  benutzerverwaltung: { enabled: true, sichtbarkeit: ['admin'] },
  einstellungen: { enabled: true, sichtbarkeit: ['admin'] }
};

// Modulberechtigungen initialisieren (falls nicht vorhanden)
export const initializeModulePermissions = async () => {
  try {
    const docRef = doc(db, "modulePermissions", MODULE_PERMISSIONS_DOC_ID);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      await setDoc(docRef, {
        ...DEFAULT_PERMISSIONS,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      console.log("Modulberechtigungen initialisiert");
      return DEFAULT_PERMISSIONS;
    }
    // Migration: Falls noch altes Format (Boolean oder String), umwandeln
    const data = docSnap.data();
    let migrated = false;
    Object.keys(DEFAULT_PERMISSIONS).forEach(key => {
      if (typeof data[key] === 'boolean') {
        data[key] = {
          enabled: data[key],
          sichtbarkeit: DEFAULT_PERMISSIONS[key].sichtbarkeit
        };
        migrated = true;
      } else if (typeof data[key] === 'object' && data[key] && typeof data[key].sichtbarkeit === 'string') {
        // Migration von String zu Array
        data[key] = {
          ...data[key],
          sichtbarkeit: [data[key].sichtbarkeit]
        };
        migrated = true;
      }
    });
    if (migrated) {
      await setDoc(docRef, { ...data, updatedAt: new Date() });
      return data;
    }
    return data;
  } catch (error) {
    console.error("Fehler beim Initialisieren der Modulberechtigungen:", error.message);
    throw error;
  }
};

// Alle Modulberechtigungen abrufen
export const getModulePermissions = async () => {
  try {
    const docRef = doc(db, "modulePermissions", MODULE_PERMISSIONS_DOC_ID);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      // Wenn keine Berechtigungen gefunden wurden, initialisieren
      return await initializeModulePermissions();
    }
    // Migration: Falls noch altes Format (Boolean oder String), umwandeln
    const data = docSnap.data();
    let migrated = false;
    Object.keys(DEFAULT_PERMISSIONS).forEach(key => {
      if (typeof data[key] === 'boolean') {
        data[key] = {
          enabled: data[key],
          sichtbarkeit: DEFAULT_PERMISSIONS[key].sichtbarkeit
        };
        migrated = true;
      } else if (typeof data[key] === 'object' && data[key] && typeof data[key].sichtbarkeit === 'string') {
        // Migration von String zu Array
        data[key] = {
          ...data[key],
          sichtbarkeit: [data[key].sichtbarkeit]
        };
        migrated = true;
      }
    });
    if (migrated) {
      await setDoc(docRef, { ...data, updatedAt: new Date() });
      return data;
    }
    return data;
  } catch (error) {
    console.error("Fehler beim Laden der Modulberechtigungen:", error.message);
    throw error;
  }
};

// Einzelne Modulberechtigung aktualisieren (enabled oder sichtbarkeit)
export const updateModulePermission = async (moduleName, updateObj) => {
  try {
    const docRef = doc(db, "modulePermissions", MODULE_PERMISSIONS_DOC_ID);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      await initializeModulePermissions();
    }
    await updateDoc(docRef, {
      [`${moduleName}`]: updateObj,
      updatedAt: new Date()
    });
    return true;
  } catch (error) {
    console.error(`Fehler beim Aktualisieren der Berechtigung für ${moduleName}:`, error.message);
    throw error;
  }
};

// Sichtbarkeit eines Moduls aktualisieren (Array-basiert)
export const updateModuleVisibility = async (moduleName, role, isVisible) => {
  try {
    const docRef = doc(db, "modulePermissions", MODULE_PERMISSIONS_DOC_ID);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      await initializeModulePermissions();
    }
    const data = docSnap.data();
    const current = data[moduleName] || { enabled: true, sichtbarkeit: ['user'] };
    
    // Sicherstellen, dass sichtbarkeit ein Array ist
    let currentSichtbarkeit = Array.isArray(current.sichtbarkeit) ? current.sichtbarkeit : [current.sichtbarkeit];
    
    let newSichtbarkeit;
    if (isVisible) {
      // Rolle hinzufügen, falls nicht bereits vorhanden
      if (!currentSichtbarkeit.includes(role)) {
        newSichtbarkeit = [...currentSichtbarkeit, role];
      } else {
        newSichtbarkeit = currentSichtbarkeit;
      }
    } else {
      // Rolle entfernen
      newSichtbarkeit = currentSichtbarkeit.filter(r => r !== role);
    }
    
    await updateDoc(docRef, {
      [`${moduleName}`]: { ...current, sichtbarkeit: newSichtbarkeit },
      updatedAt: new Date()
    });
    return true;
  } catch (error) {
    console.error(`Fehler beim Aktualisieren der Sichtbarkeit für ${moduleName}:`, error.message);
    throw error;
  }
};

// Alle Modulberechtigungen auf einmal aktualisieren
export const updateAllModulePermissions = async (permissions) => {
  try {
    const docRef = doc(db, "modulePermissions", MODULE_PERMISSIONS_DOC_ID);
    
    // Alle Berechtigungen aktualisieren
    await updateDoc(docRef, {
      ...permissions,
      updatedAt: new Date()
    });
    
    return true;
  } catch (error) {
    console.error("Fehler beim Aktualisieren aller Modulberechtigungen:", error.message);
    throw error;
  }
};

// Alle Module aktivieren
export const enableAllModules = async () => {
  try {
    const docRef = doc(db, "modulePermissions", MODULE_PERMISSIONS_DOC_ID);
    
    const enabledModules = {};
    Object.keys(DEFAULT_PERMISSIONS).forEach(module => {
      enabledModules[module] = { enabled: true, sichtbarkeit: DEFAULT_PERMISSIONS[module].sichtbarkeit };
    });
    
    await updateDoc(docRef, {
      ...enabledModules,
      updatedAt: new Date()
    });
    
    return enabledModules;
  } catch (error) {
    console.error("Fehler beim Aktivieren aller Module:", error.message);
    throw error;
  }
};

// Alle Module deaktivieren
export const disableAllModules = async () => {
  try {
    const docRef = doc(db, "modulePermissions", MODULE_PERMISSIONS_DOC_ID);
    
    const disabledModules = {};
    Object.keys(DEFAULT_PERMISSIONS).forEach(module => {
      disabledModules[module] = { enabled: false, sichtbarkeit: DEFAULT_PERMISSIONS[module].sichtbarkeit };
    });
    
    await updateDoc(docRef, {
      ...disabledModules,
      updatedAt: new Date()
    });
    
    return disabledModules;
  } catch (error) {
    console.error("Fehler beim Deaktivieren aller Module:", error.message);
    throw error;
  }
};

// Sidebar-Menü-Reihenfolge zentral speichern/lesen
export const getSidebarOrder = async () => {
  try {
    const docRef = doc(db, "modulePermissions", MODULE_PERMISSIONS_DOC_ID);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && Array.isArray(docSnap.data().sidebarOrder)) {
      return docSnap.data().sidebarOrder;
    }
    return null; // Noch keine Reihenfolge gespeichert
  } catch (error) {
    console.error("Fehler beim Laden der Sidebar-Reihenfolge:", error.message);
    throw error;
  }
};

export const setSidebarOrder = async (orderArray) => {
  try {
    const docRef = doc(db, "modulePermissions", MODULE_PERMISSIONS_DOC_ID);
    await updateDoc(docRef, {
      sidebarOrder: orderArray,
      updatedAt: new Date()
    });
    return true;
  } catch (error) {
    console.error("Fehler beim Speichern der Sidebar-Reihenfolge:", error.message);
    throw error;
  }
};